import React, { useEffect, useMemo, useState } from "react";
import { X, Search } from "lucide-react";
import StrategyDiagram from "./StrategyDiagram";
import { decorateTemplates, groupByComplexity, COMPLEXITY, GROUP_ORDER } from "./strategyMeta";

// Risk tags shown for the currently-hovered strategy at the bottom of the
// modal — matches OptionStrat's "Limited Profit / Limited Loss" chips.
const RISK_TAGS = {
  long_call:               ["Bullish", "Limited Loss", "Unlimited Profit"],
  long_put:                ["Bearish", "Limited Loss", "Limited Profit"],
  covered_call:            ["Neutral", "Limited Loss", "Limited Profit"],
  cash_secured_put:        ["Bullish", "Limited Loss", "Limited Profit"],
  bull_call_spread:        ["Bullish", "Limited Loss", "Limited Profit"],
  bear_put_spread:         ["Bearish", "Limited Loss", "Limited Profit"],
  bull_put_spread:         ["Bullish", "Limited Loss", "Limited Profit"],
  bear_call_spread:        ["Bearish", "Limited Loss", "Limited Profit"],
  iron_condor:             ["Neutral", "Limited Loss", "Limited Profit"],
  iron_butterfly:          ["Neutral", "Limited Loss", "Limited Profit"],
  call_butterfly:          ["Neutral", "Limited Loss", "Limited Profit"],
  put_butterfly:           ["Neutral", "Limited Loss", "Limited Profit"],
  long_straddle:           ["Neutral", "Limited Loss", "Unlimited Profit"],
  long_strangle:           ["Neutral", "Limited Loss", "Unlimited Profit"],
  calendar_call:           ["Neutral", "Limited Loss", "Limited Profit"],
  diagonal_call:           ["Bullish", "Limited Loss", "Limited Profit"],
  leaps_call:              ["Bullish", "Limited Loss", "Unlimited Profit"],
  synthetic_long:          ["Bullish", "Unlimited Loss", "Unlimited Profit"],
  ratio_call_spread:       ["Neutral", "Unlimited Loss", "Limited Profit"],
  ratio_put_spread:        ["Neutral", "Limited Loss", "Limited Profit"],
  call_ratio_backspread:   ["Bullish", "Limited Loss", "Unlimited Profit"],
  put_ratio_backspread:    ["Bearish", "Limited Loss", "Limited Profit"],
  broken_wing_butterfly:   ["Neutral", "Limited Loss", "Limited Profit"],
  jade_lizard:             ["Neutral", "Unlimited Loss", "Limited Profit"],
  reverse_jade_lizard:     ["Neutral", "Unlimited Loss", "Limited Profit"],
  double_diagonal:         ["Neutral", "Limited Loss", "Limited Profit"],
  double_calendar:         ["Neutral", "Limited Loss", "Limited Profit"],
  zebra_call:              ["Bullish", "Limited Loss", "Unlimited Profit"],
  zebra_put:               ["Bearish", "Limited Loss", "Limited Profit"],
  box_spread:              ["Neutral", "Limited Loss", "Limited Profit"],
  collar:                  ["Neutral", "Limited Loss", "Limited Profit"],
  strap:                   ["Bullish", "Limited Loss", "Unlimited Profit"],
  strip:                   ["Bearish", "Limited Loss", "Unlimited Profit"],
};

const tagClass = (tag) => {
  if (tag === "Bullish") return "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30";
  if (tag === "Bearish") return "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/30";
  if (tag === "Neutral") return "bg-white/8 text-white/70 border-white/15";
  if (tag.startsWith("Unlimited L")) return "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/30";
  if (tag.startsWith("Limited L")) return "bg-[#FACC15]/15 text-[#FACC15] border-[#FACC15]/30";
  if (tag.startsWith("Unlimited P")) return "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30";
  if (tag.startsWith("Limited P")) return "bg-[#22C55E]/10 text-[#86EFAC] border-[#22C55E]/25";
  return "bg-white/10 text-white/70 border-white/15";
};

/**
 * Full-screen 4/5-column strategy picker overlay matching
 * optionstrat.com/build/. Columns = complexity (Novice/Intermediate/Advanced
 * /Expert), each with its own group headers (Basic, Income, Credit Spreads,
 * etc). Search box filters across all columns simultaneously. Hovering a
 * row shows a synopsis + risk tags in the bottom drawer.
 */
const StrategyPickerModal = ({ open, onClose, templates = [], activeId, onPick }) => {
  const [q, setQ] = useState("");
  const [hover, setHover] = useState(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const decorated = useMemo(() => decorateTemplates(templates), [templates]);

  const filtered = useMemo(() => {
    const f = q.trim().toLowerCase();
    if (!f) return decorated;
    return decorated.filter((t) =>
      `${t.name} ${t.group} ${t.complexity} ${t.bias}`.toLowerCase().includes(f),
    );
  }, [decorated, q]);

  const byComplexity = useMemo(() => groupByComplexity(filtered), [filtered]);

  const hovered = hover || decorated.find((t) => t.id === activeId) || decorated[0];

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#050912]/85 backdrop-blur-md flex items-stretch justify-center"
      data-testid="forge-picker-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[1500px] mx-3 my-6 bg-[#0B1426] border border-white/10 rounded-xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/10 bg-[#0E1A30]">
          <div className="text-[13px] uppercase tracking-[0.22em] text-white/80 font-medium">
            Pick a strategy
          </div>
          <div className="relative flex-1 max-w-md ml-4">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              autoFocus
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 35+ strategies — iron condor, butterfly, …"
              className="w-full bg-[#050912] border border-white/10 focus:border-[#3B82F6] outline-none rounded-md pl-9 pr-3 py-1.5 text-[12.5px] text-white placeholder-white/35"
              data-testid="forge-picker-search"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="forge-picker-close"
            className="ml-auto inline-flex items-center justify-center w-8 h-8 rounded-md border border-white/10 text-white/60 hover:text-white hover:bg-white/5 transition"
            aria-label="Close strategy picker"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Columns */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 overflow-hidden">
          {COMPLEXITY.map((c) => {
            const groups = byComplexity[c] || {};
            const order = GROUP_ORDER[c] || [];
            const groupKeys = [
              ...order.filter((g) => groups[g]),
              ...Object.keys(groups).filter((g) => !order.includes(g)),
            ];
            return (
              <div
                key={c}
                className="bg-[#0B1426] overflow-y-auto"
                data-testid={`forge-picker-col-${c.toLowerCase()}`}
              >
                <div className="sticky top-0 z-10 bg-[#0E1A30]/95 backdrop-blur px-4 py-2.5 border-b border-white/10">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/90 font-medium">
                    {c}
                  </div>
                </div>
                <div className="py-2">
                  {groupKeys.length === 0 && (
                    <div className="text-[11px] text-white/30 italic px-4 py-3">
                      Nothing here.
                    </div>
                  )}
                  {groupKeys.map((g) => (
                    <div key={g} className="mb-2">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 px-4 mb-1">
                        {g}
                      </div>
                      <div>
                        {(groups[g] || []).map((t) => {
                          const isActive = t.id === activeId;
                          return (
                            <button
                              key={t.id}
                              type="button"
                              onMouseEnter={() => setHover(t)}
                              onClick={() => onPick(t)}
                              data-testid={`forge-picker-strat-${t.id}`}
                              className={`group w-full flex items-center gap-2.5 px-4 py-1.5 text-left transition ${
                                isActive
                                  ? "bg-[#3B82F6]/15 text-white"
                                  : "text-white/75 hover:bg-white/[0.05] hover:text-white"
                              }`}
                            >
                              <StrategyDiagram profile={t.payoff} />
                              <span className="flex-1 text-[12.5px] truncate">{t.name}</span>
                              {t.pro_only && (
                                <span className="text-[9px] text-[#FACC15]">PRO</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer synopsis */}
        {hovered && (
          <div
            className="border-t border-white/10 bg-[#0E1A30] px-5 py-3 flex items-center gap-4"
            data-testid="forge-picker-synopsis"
          >
            <StrategyDiagram profile={hovered.payoff} className="!w-10 !h-7" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[13px] text-white font-medium">{hovered.name}</span>
                {(RISK_TAGS[hovered.id] || []).map((tag) => (
                  <span
                    key={tag}
                    className={`text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border ${tagClass(tag)}`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="text-[11.5px] text-white/55 truncate">{hovered.description}</div>
            </div>
            <button
              type="button"
              onClick={() => onPick(hovered)}
              data-testid="forge-picker-confirm"
              className="ml-auto px-4 py-2 rounded-md bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] uppercase tracking-[0.18em] font-medium transition"
            >
              Build →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategyPickerModal;
